import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { filter, finalize, forkJoin, switchMap } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import StatusEnum = Boo.Objects.Enums.StatusEnum;
import CustomerUser = Boo.Objects.CustomerUser;
import UserLevelEnum = Boo.Objects.Enums.UserLevelEnum;
import PartnerEnum = Boo.Objects.Enums.PartnerEnum;
import { UserService } from '../../../services/user.service';
import { CustomerUserService } from '../../../services/customer-user.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { ConfirmBox } from '../../shared/ConfirmBox/confirm-box';
import { CustomerUserComponent } from '../CustomerUser/CustomerUser.component';


@Component({
  selector: 'app-components-managecustomer-reporting-dashboard-access',
  templateUrl: './reporting-dashboard-access.component.html'
})
export class ReportingDashboardAccessComponent implements OnInit {
  @Input() user: Boo.Objects.User;
  @Input() partner: Boo.Objects.Partner;
  @Input() customer: Boo.Objects.Customer;
  @Output() refreshCustomer = new EventEmitter<void>();
  isLoading = false;
  showResendLogin = false;
  canViewOnboardingLink = false;
  countryAbbreviation: string;
  users: CustomerUser[];
  ssoUrls: Record<number, string> = {};

  constructor(
    private userService: UserService,
    private customerUserService: CustomerUserService,
    private sessionStorageService: SessionStorageService,
    private modalService: NgbModal,
    private confirmBox: ConfirmBox) { }

  ngOnInit(): void {
    this.showResendLogin = this.customer.Partner.IsWhiteLabel
      && !this.customer.Partner.IsSingleSignonRequired
      && this.customer.StatusId !== StatusEnum.Canceled;

    this.canViewOnboardingLink = this.customer.AllProducts?.includes(Boo.Objects.Enums.ProductEnum.StarterSeo);

    if (this.partner.PartnerId !== PartnerEnum.Boostability) {
      this.users = _.filter(this.customer.Users, (user) => {
        return user.UserLevelId !== UserLevelEnum.Specialist && !user.IsReportingUser;
      });
    } else {
      this.users = _.filter(this.customer.Users, (user) => {
        return (user.UserLevelId === UserLevelEnum.Analytics || user.UserId === this.user.UserId) && !user.IsReportingUser;
      });
    }

    this.users = this.users.sort((a, b) => Number(b.CanContact) - Number(a.CanContact));

    this.loadSingleSignOnUrls();

    this.isLoading = true;

    forkJoin([
      this.sessionStorageService.getStaticData(),
      this.customerUserService.getByIds(this.customer.Users.map(u => u.UserId))
    ]).pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: ([staticData, customerUsers]) => {
          const customerCountry = staticData.Countries.find((c: any) => c.CountryId === this.customer.CountryId);
          this.countryAbbreviation = customerCountry ? customerCountry.Abbreviation : 'US';
          this.customer.Users.forEach(u => {
            u.CustomerUsers = customerUsers.filter((cu: any) => cu.UserId === u.UserId);
            u.EmailRecipientType = customerUsers.find((cu: any) => cu.CustomerUserId === u.CustomerUserId)?.EmailRecipientType;
          });
        },
        error: error => toastr.error(error)
      });
  }

  private loadSingleSignOnUrls(): void {
    if (!this.users.length) {
      return;
    }

    forkJoin(this.users.map(x => this.customerUserService.getSingleSignOnUrl(this.customer.CustomerId, x.CustomerUserId)))
      .subscribe({
        next: urls => this.users.forEach((x, i) => this.ssoUrls[x.CustomerUserId] = urls[i]),
        error: error => toastr.error(error)
      });
  }

  impersonateUser(impersonateUser: Boo.Objects.CustomerUser): void {
    this.userService.impersonate(impersonateUser.CustomerUserId)
      .subscribe({
        next: token => window.open(`${this.customer.Partner.ReportingUrl}/?t=${token}`, '_blank'),
        error: error => toastr.error(error, 'Impersonated User')
      });
  }

  sendPasswordResetEmail(user: Boo.Objects.CustomerUser): void {
    const fromAddress = this.customer.Partner.SupportEmailAddress || '<em>(partner support email not configured)</em>';
    const message = `Send a password reset email?<br><br>`
      + `<strong>To:</strong> ${user.FullName} (${user.Username})<br>`
      + `<strong>From:</strong> ${fromAddress}`;

    this.confirmBox.confirm(message, { title: 'Send Password Reset Email' })
      .pipe(
        filter(confirmed => confirmed),
        switchMap(() => {
          this.isLoading = true;
          return this.userService.sendPasswordResetEmail(user.Username, false);
        }),
        finalize(() => this.isLoading = false)
      ).subscribe({
        next: () => toastr.success('Password reset instructions have been sent to the email address.', 'Password Reset'),
        error: error => toastr.error(error, 'Password Reset')
      });
  }

  deleteUser(customerUser: CustomerUser): void {
    if (customerUser.CanContact) {
      toastr.warning(`Cannot delete the dashboard user for a contact. Remove them from the customer contacts.`);
      return;
    }

    this.confirmBox.confirm('Are you sure you want to delete this user? This action cannot be undone.', { title: 'Delete User' })
      .pipe(
        filter(confirmed => confirmed),
        switchMap(() => {
          this.isLoading = true;
          return this.customerUserService.delete(customerUser.CustomerUserId);
        }),
        finalize(() => this.isLoading = false)
      ).subscribe(() => {
        toastr.success('Successfully deleted user.');

        this.refreshCustomer.emit();
        this.customer.Users = this.customer.Users.filter(u => u !== customerUser);
        this.users = this.users.filter(u => u !== customerUser);
      });
  }

  addUser(): void {
    const newUser = {
      IsPrimary: false,
      CanContact: false,
      CanReceiveTextMessages: false,
      CustomerId: this.customer.CustomerId,
      CustomerUserId: 0,
      CustomerUserPhoneNumbers: [],
      EmailRecipientType: null,
      InsertedDate: new Date(),
      IsCustomerUserActive: true,
      UserLevelId: UserLevelEnum.Analytics,
      PrimaryPhoneNumber: '',
      FullName: '',
      UserId: 0,
      Username: '',
      Password: '',
      FirstName: '',
      LastName: '',
      PhoneExtension: '',
      IsActive: true,
      AccessToken: '',
      CustomerUsers: [],
      PartnerUsers: [],
      Customers: [],
      Languages: [],
      Partners: [],
      UserPermissions: [],
      IsReportingUser: false
    } as CustomerUser;

    this.openUserModal(newUser);
  }

  editUser(customerUser: CustomerUser): void {
    this.openUserModal(customerUser);
  }

  private openUserModal(customerUser: CustomerUser): void {
    const modalRef = this.modalService.open(CustomerUserComponent, { size: 'lg' });

    modalRef.componentInstance.customer = this.customer;
    modalRef.componentInstance.customerUser = customerUser;
    modalRef.componentInstance.partner = this.partner;
    modalRef.componentInstance.countryAbbreviation = this.countryAbbreviation;
    modalRef.componentInstance.refreshCustomer = this.refreshCustomer;

    modalRef.result.then(
      (newCustomerUser: CustomerUser) => {
        this.refreshCustomer.emit();

        if (newCustomerUser) {
          this.users.push(newCustomerUser);
        }
      },
      () => { /* no action needed on dismiss */ }
    );
  }
}
